import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import ProcessShowcase from "@/components/ProcessShowcase";
import { ToenungScene, TOENUNG_STEPS } from "@/components/processScenes";
import { WORKSHOP_VIDEOS } from "@/data/workshopVideos";
import SeoHead from "@/components/SeoHead";
import AutocompleteInput from "@/components/AutocompleteInput";
import { searchBrands, searchModels } from "@/data/vehicles";
import { SITE_URL } from "@/seo/data";
import api from "@/lib/api";
import { isValidEmail } from "@/lib/validate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  Car, CurrencyEur, CalendarCheck, UserCircle, CheckCircle, CaretDown,
  ShieldCheck, Sparkle, Sun, Eye, Drop, SealCheck, ArrowRight, MapPin,
} from "@phosphor-icons/react";

const YEARS = Array.from({ length: 32 }, (_, i) => String(new Date().getFullYear() - i));

const BENEFITS = [
  { icon: Sparkle, label: "Hochwertige Tönungsfolie" },
  { icon: ShieldCheck, label: "Professionelle Montage" },
  { icon: SealCheck, label: "ABG / Prüfnummer" },
  { icon: Eye, label: "Mehr Sichtschutz" },
  { icon: Sun, label: "Weniger Blendung" },
  { icon: Drop, label: "Hitze- & UV-Schutz je nach Folie" },
  { icon: CurrencyEur, label: "Verbindlicher Festpreis" },
  { icon: CalendarCheck, label: "Termin direkt online buchen" },
];

const STEPS = ["Fahrzeug", "Preis", "Termin", "Daten", "Bestätigung"];

const AEO_QA = [
  { q: "Was kostet eine Scheibentönung?", a: "Bei Servus Autoglas kostet die professionelle Scheibentönung je nach Fahrzeugklasse zwischen 250 € und 420 €. Nach Auswahl Ihres Fahrzeugs sehen Sie direkt Ihren verbindlichen Festpreis inklusive Material, Montage und Mehrwertsteuer." },
  { q: "Wann kann ich einen Termin für Scheibentönung buchen?", a: "Scheibentönungen werden bei Servus Autoglas montags durchgeführt. Freie Montagstermine können direkt online ausgewählt und verbindlich gebucht werden." },
  { q: "Welche Scheiben werden getönt?", a: "Standardmäßig werden die zulässigen hinteren Seitenscheiben ab der B-Säule sowie die Heckscheibe foliert." },
  { q: "Bekomme ich den Preis sofort?", a: "Ja. Nach Auswahl von Hersteller, Modell und Fahrzeugvariante wird der Festpreis unmittelbar angezeigt." },
];

const FAQS = [
  { q: "Was kostet Scheibentönung bei Servus Autoglas?", a: "Je nach Fahrzeugklasse zwischen 250 € (Kleinwagen) und 420 € (großer Van/Transporter) — als verbindlicher Festpreis inklusive hochwertiger Tönungsfolie, professioneller Montage und 19 % MwSt. Nach Fahrzeugauswahl sehen Sie Ihren exakten Preis sofort online." },
  { q: "Warum kostet ein SUV mehr als ein Kleinwagen?", a: "SUVs, Kombis und Vans haben deutlich größere und oft stärker gewölbte Glasflächen sowie mehr Scheiben. Das bedeutet mehr Folienmaterial und mehr Arbeitszeit für eine perfekte, blasenfreie Verlegung." },
  { q: "Welche Autoscheiben dürfen getönt werden?", a: "Nachträglich getönt werden dürfen die Scheiben hinter dem Fahrerbereich: die hinteren Seitenscheiben ab der B-Säule sowie die Heckscheibe. Genau das ist unser Standardpaket." },
  { q: "Werden Frontscheibe oder vordere Seitenscheiben getönt?", a: "Nein. Windschutzscheibe und vordere Seitenscheiben dürfen in Deutschland nicht mit normaler Tönungsfolie beklebt werden. Wir bieten dies daher bewusst nicht an." },
  { q: "Was bedeutet ABG bei einer Tönungsfolie?", a: "ABG steht für Allgemeine Bauartgenehmigung. Nur Folien mit dieser Genehmigung dürfen auf Fahrzeugscheiben im Straßenverkehr eingesetzt werden. Wir verwenden ausschließlich für den Einsatzzweck zugelassene Folien." },
  { q: "Wo finde ich die Prüfnummer meiner Tönungsfolie?", a: "Die Prüf- bzw. Genehmigungskennzeichnung befindet sich direkt auf der verbauten Folie und bleibt entsprechend den gesetzlichen Vorgaben sichtbar. Zusätzlich erhalten Sie die zugehörigen Unterlagen von uns ausgehändigt, soweit erforderlich." },
  { q: "Muss eine Tönungsfolie beim TÜV eingetragen werden?", a: "Eine Folie mit gültiger ABG muss nicht in die Fahrzeugpapiere eingetragen werden. Wichtig ist, dass die Prüfkennzeichnung auf der Folie sichtbar bleibt und die ABG-Unterlagen im Fahrzeug mitgeführt werden." },
  { q: "Wie lange dauert eine professionelle Scheibentönung?", a: "Je nach Fahrzeug und Scheibenanzahl in der Regel einige Stunden. Sie bringen Ihr Fahrzeug morgens zum gebuchten Termin und wir melden uns, sobald es fertig ist." },
  { q: "Welche Vorteile hat eine Tönungsfolie?", a: "Mehr Privatsphäre, weniger Blendung, Schutz des Innenraums vor Sonneneinstrahlung und ein hochwertigerer Look. Je nach eingesetzter Folie können zusätzlich Wärme- und UV-Einstrahlung reduziert werden." },
  { q: "Hilft Scheibentönung gegen Hitze?", a: "Hochwertige Tönungsfolien können die Wärmeeinstrahlung in den Innenraum spürbar reduzieren — der genaue Effekt hängt von der konkret eingesetzten Folie ab. Die technischen Eigenschaften der bei Ihnen verwendeten Folie besprechen wir transparent vor der Montage." },
  { q: "Schützt Tönungsfolie vor UV-Strahlung?", a: "Professionelle Folien können UV-Strahlung deutlich reduzieren. Konkrete Prozentwerte nennen wir nur, wenn sie vom Folienhersteller für das eingesetzte Produkt bestätigt sind." },
  { q: "Kann ich den Tönungsgrad auswählen?", a: "Sofern mehrere Folienvarianten verfügbar sind, können Sie den gewünschten Tönungsgrad bei der Buchung wählen — von dezenter Abdunkelung bis zu starkem Privacy-Effekt. Wir beraten Sie gerne." },
  { q: "Wann darf ich die Fenster nach der Folierung wieder öffnen?", a: "Wir empfehlen, die Fenster in den ersten Tagen nach der Montage geschlossen zu halten, damit die Folie vollständig trocknen und sich optimal mit der Scheibe verbinden kann. Den genauen Zeitraum nennen wir Ihnen bei der Abholung." },
  { q: "Wie pflege ich frisch folierte Scheiben?", a: "In den ersten Wochen keine aggressiven Reiniger und keine scheuernden Schwämme verwenden. Danach reichen ein weiches Mikrofasertuch und ein mildes Reinigungsmittel völlig aus." },
  { q: "Gibt es Garantie auf die Tönungsfolie?", a: "Garantieangaben richten sich nach dem konkret verbauten Folienprodukt und dessen Hersteller. Die für Ihre Folie geltenden Garantiebedingungen erhalten Sie transparent bei der Auftragsausführung." },
  { q: "Kann ich meinen Termin online buchen?", a: "Ja — Fahrzeug auswählen, Festpreis sehen, freien Montag und Uhrzeit wählen und verbindlich buchen. Sie erhalten sofort eine Bestätigung per E-Mail." },
  { q: "Warum kann ich nur montags buchen?", a: "Wir bündeln alle Folierungstermine auf den Montag. So arbeitet unser Folien-Team in Ruhe und höchster Qualität, während der restliche Werkstattbetrieb für Autoglas-Reparaturen freigehalten wird." },
];

export default function Toenung() {
  const [step, setStep] = useState(0);
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [year, setYear] = useState("");
  const [quote, setQuote] = useState(null);
  const [bodyOverride, setBodyOverride] = useState("");
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [filmId, setFilmId] = useState("");
  const [days, setDays] = useState([]);
  const [selDate, setSelDate] = useState("");
  const [selTime, setSelTime] = useState("");
  const [form, setForm] = useState({ first_name: "", last_name: "", phone: "", email: "", license_plate: "", note: "" });
  const [confirm, setConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [booking, setBooking] = useState(null);
  const [openFaq, setOpenFaq] = useState(-1);

  const fetchQuote = async (override = "") => {
    if (!make.trim() || !model.trim()) {
      toast.error("Bitte Hersteller und Modell angeben");
      return;
    }
    setLoadingQuote(true);
    try {
      const { data } = await api.post("/tint/quote", {
        manufacturer: make, model, year, body_override: override || undefined,
      });
      setQuote(data);
      setStep(1);
    } catch {
      toast.error("Preis konnte nicht berechnet werden");
    } finally {
      setLoadingQuote(false);
    }
  };

  const loadAvailability = async () => {
    try {
      const { data } = await api.get("/tint/availability");
      setDays(data.days || []);
      setStep(2);
    } catch {
      toast.error("Termine konnten nicht geladen werden");
    }
  };

  const submitBooking = async (withDeposit) => {
    for (const [k, label] of [["first_name", "Vorname"], ["last_name", "Nachname"], ["phone", "Telefonnummer"], ["email", "E-Mail-Adresse"]]) {
      if (!form[k].trim()) {
        toast.error(`Bitte ${label} angeben`);
        return;
      }
    }
    if (!isValidEmail(form.email)) {
      toast.error("Bitte eine gültige E-Mail-Adresse angeben (z.B. max@beispiel.de)");
      return;
    }
    if (!confirm) {
      toast.error("Bitte bestätigen Sie die verbindliche Buchung");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/tint/bookings", {
        manufacturer: make, model, year,
        body_override: bodyOverride || undefined,
        film_id: filmId || undefined,
        date: selDate, time: selTime,
        ...form, email: form.email.trim().toLowerCase(), confirm: true,
      });
      setBooking(data);
      if (withDeposit) {
        try {
          const { data: pay } = await api.post(`/tint/bookings/${data.id}/checkout`, {
            origin_url: window.location.origin,
          });
          window.location.href = pay.url;
          return;
        } catch {
          toast.error("Zahlung konnte nicht gestartet werden — Ihre Buchung ist trotzdem bestätigt.");
        }
      }
      setStep(4);
      window.scrollTo(0, 0);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Die Buchung konnte gerade nicht abgeschlossen werden. Ihre Zahlung wurde nicht doppelt ausgelöst. Bitte versuchen Sie es erneut oder kontaktieren Sie uns.";
      toast.error(msg);
      if (err?.response?.status === 409) {
        loadAvailability();
        setSelTime("");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const priceStr = quote ? `${quote.price.toLocaleString("de-DE")} €` : "";
  const depositStr = quote ? `${quote.deposit.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €` : "";
  const restStr = quote ? `${(quote.price - quote.deposit).toLocaleString("de-DE", { minimumFractionDigits: 2 })} €` : "";
  const fmtDate = (d) => {
    const [y, m, dd] = d.split("-");
    return new Date(Number(y), Number(m) - 1, Number(dd), 12).toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  };
  const activeFilms = quote?.films || [];

  const schema = useMemo(() => ([
    {
      "@context": "https://schema.org", "@type": "Service",
      name: "Scheibentönung ab B-Säule inkl. Heckscheibe",
      serviceType: "Autoscheiben tönen",
      provider: { "@type": "AutoRepair", name: "Servus Autoglas — AutoGlas Erol GmbH", address: { "@type": "PostalAddress", streetAddress: "Frankenthaler Str. 98", postalCode: "67059", addressLocality: "Ludwigshafen am Rhein", addressCountry: "DE" }, telephone: "+49 621 527429" },
      areaServed: [{ "@type": "City", name: "Ludwigshafen" }, { "@type": "City", name: "Mannheim" }, { "@type": "City", name: "Frankenthal" }, { "@type": "City", name: "Heidelberg" }],
      offers: { "@type": "AggregateOffer", priceCurrency: "EUR", lowPrice: "250", highPrice: "420", description: "Festpreis je nach Fahrzeugklasse, inkl. Material, Montage und MwSt." },
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: "Scheibentönung — Festpreise nach Fahrzeugklasse",
        itemListElement: [
          { "@type": "Offer", name: "Scheibentönung Kleinwagen", price: "250", priceCurrency: "EUR" },
          { "@type": "Offer", name: "Scheibentönung Kompaktklasse", price: "290", priceCurrency: "EUR" },
          { "@type": "Offer", name: "Scheibentönung Limousine / Coupé", price: "320", priceCurrency: "EUR" },
          { "@type": "Offer", name: "Scheibentönung Kombi", price: "340", priceCurrency: "EUR" },
          { "@type": "Offer", name: "Scheibentönung SUV", price: "350", priceCurrency: "EUR" },
          { "@type": "Offer", name: "Scheibentönung Van", price: "390", priceCurrency: "EUR" },
          { "@type": "Offer", name: "Scheibentönung Transporter", price: "420", priceCurrency: "EUR" },
        ],
      },
    },
    {
      "@context": "https://schema.org", "@type": "FAQPage",
      mainEntity: [...AEO_QA, ...FAQS].map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
    },
    {
      "@context": "https://schema.org", "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Start", item: SITE_URL + "/" },
        { "@type": "ListItem", position: 2, name: "Scheibentönung", item: SITE_URL + "/scheibentoenung" },
      ],
    },
  ]), []);

  return (
    <div className="min-h-screen bg-slate-50">
      <SeoHead
        title="Scheibentönung Mannheim & Ludwigshafen ab 250 € Festpreis | Servus Autoglas"
        description="Scheiben tönen lassen zum Festpreis ab 250 € inkl. Folie, Montage & MwSt. Fahrzeug wählen, Preis sofort online sehen, Montagstermin direkt buchen. Hochwertige Tönungsfolie mit ABG — Ludwigshafen & Mannheim."
        canonical={`${SITE_URL}/scheibentoenung`}
      />
      <Helmet>
        {schema.map((s, i) => (
          <script key={i} type="application/ld+json">{JSON.stringify(s)}</script>
        ))}
      </Helmet>
      <Header />

      {/* HERO + KONFIGURATOR */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-pink-100 border border-pink-200 text-pink-700 text-xs font-bold uppercase tracking-widest">
            <Sparkle size={14} weight="fill" /> Festpreis-Konfigurator
          </span>
          <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight">
            Autoscheiben tönen in Mannheim &amp; Ludwigshafen — Festpreis sofort online
          </h1>
          <p className="mt-4 text-slate-600 text-lg">
            Fahrzeug auswählen → Festpreis erhalten → Montag auswählen → verbindlich buchen
          </p>

          {/* Stepper */}
          <div className="mt-8 flex items-center gap-1 sm:gap-2 overflow-x-auto" data-testid="tint-stepper">
            {STEPS.map((s, i) => (
              <div key={s} className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold ${i === step ? "bg-pink-600 text-white" : i < step ? "bg-pink-100 text-pink-700" : "bg-slate-100 text-slate-400"}`}>
                  <span>{i + 1}</span><span className="hidden sm:inline">{s}</span>
                </div>
                {i < STEPS.length - 1 && <div className={`w-4 h-0.5 ${i < step ? "bg-pink-400" : "bg-slate-200"}`} />}
              </div>
            ))}
          </div>

          <div className="mt-8 bg-slate-50 rounded-3xl border border-slate-200 p-6 md:p-8" data-testid="tint-configurator">
            {/* SCHRITT 1: FAHRZEUG */}
            {step === 0 && (
              <div className="space-y-5 max-w-xl">
                <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2"><Car size={24} className="text-pink-600" /> Ihr Fahrzeug</h2>
                <div>
                  <label className="text-sm font-semibold text-slate-700">Hersteller *</label>
                  <AutocompleteInput value={make} onChange={setMake} getSuggestions={(q) => searchBrands(q, 10)} placeholder="z.B. BMW, Volkswagen, Mercedes-Benz" testId="tint-make" />
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-700">Modell *</label>
                  <AutocompleteInput value={model} onChange={setModel} getSuggestions={(q) => searchModels(make, q, 12)} placeholder="z.B. X3, Golf, C-Klasse" testId="tint-model" />
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-700">Baujahr (optional)</label>
                  <select value={year} onChange={(e) => setYear(e.target.value)} className="mt-1 w-full h-11 rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="tint-year">
                    <option value="">Bitte wählen</option>
                    {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                <Button onClick={() => { setBodyOverride(""); fetchQuote(); }} disabled={loadingQuote} className="h-12 px-8 bg-pink-600 hover:bg-pink-700 text-white font-semibold rounded-xl" data-testid="tint-quote-button">
                  {loadingQuote ? "Berechne…" : "Festpreis berechnen"} <ArrowRight size={18} className="ml-2" />
                </Button>
                <p className="text-xs text-slate-500">Ihr Fahrzeug ist nicht dabei? <a href="https://wa.me/4915222717582" target="_blank" rel="noreferrer" className="text-pink-600 font-semibold">Kontaktieren Sie uns</a> — wir helfen Ihnen gerne weiter.</p>
              </div>
            )}

            {/* SCHRITT 2: PREIS */}
            {step === 1 && quote && (
              <div className="space-y-5">
                {!quote.confident && (
                  <div className="max-w-xl">
                    <p className="text-sm font-semibold text-slate-700 mb-2">Zur genauen Zuordnung: Welche Karosserieform hat Ihr {make} {model}?</p>
                    <div className="flex flex-wrap gap-2" data-testid="tint-body-select">
                      {(quote.classes || []).map((c) => (
                        <button key={c.key} onClick={() => { setBodyOverride(c.key); fetchQuote(c.key); }} className={`px-4 py-2 rounded-full border text-sm font-medium transition-colors ${bodyOverride === c.key ? "bg-pink-600 text-white border-pink-600" : "bg-white border-slate-200 hover:border-pink-300 text-slate-700"}`}>
                          {c.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div className="rounded-2xl bg-white border-2 border-pink-200 p-6 md:p-8 max-w-xl" data-testid="tint-price-box">
                  <p className="text-xs font-bold text-pink-600 uppercase tracking-widest">Ihre Scheibentönung</p>
                  <h2 className="mt-2 text-2xl font-bold text-slate-900">{make} {model}{year ? ` (${year})` : ""} — {quote.class_label}</h2>
                  <p className="text-slate-600 mt-1">Scheibentönung ab B-Säule inkl. Heckscheibe</p>
                  <div className="mt-4 flex items-end gap-2">
                    <span className="text-5xl font-bold text-pink-600" data-testid="tint-price">{priceStr}</span>
                    <span className="text-slate-500 font-semibold mb-1.5">Festpreis</span>
                  </div>
                  <p className="text-sm text-slate-500">inkl. Material, Montage und MwSt.</p>
                  <ul className="mt-5 space-y-2">
                    {["Hochwertige Tönungsfolie", "Folierung der zulässigen hinteren Seitenscheiben + Heckscheibe", "Professionelle Montage & Endkontrolle", "ABG-Unterlagen & gesetzliche Prüfkennzeichnung", "Festpreis ohne versteckte Zusatzkosten"].map((t) => (
                      <li key={t} className="flex items-start gap-2 text-sm text-slate-700"><CheckCircle size={18} weight="fill" className="text-emerald-500 mt-0.5 flex-shrink-0" />{t}</li>
                    ))}
                  </ul>
                  {activeFilms.length > 1 && (
                    <div className="mt-5">
                      <p className="text-sm font-semibold text-slate-700 mb-2">Tönungsgrad wählen</p>
                      <div className="flex flex-wrap gap-2">
                        {activeFilms.map((f) => (
                          <button key={f.id} onClick={() => setFilmId(f.id)} className={`px-4 py-2 rounded-xl border text-sm text-left ${filmId === f.id ? "border-pink-600 bg-pink-50" : "border-slate-200 bg-white"}`}>
                            <span className="font-semibold">{f.toenungsgrad || f.serie}</span>
                            {f.lichtdurchlaessigkeit && <span className="block text-xs text-slate-500">Lichtdurchlässigkeit: {f.lichtdurchlaessigkeit}</span>}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <Button onClick={loadAvailability} className="mt-6 w-full h-12 bg-pink-600 hover:bg-pink-700 text-white font-semibold rounded-xl" data-testid="tint-to-calendar">
                    Montagstermin auswählen <ArrowRight size={18} className="ml-2" />
                  </Button>
                </div>
                <button onClick={() => { setStep(0); setQuote(null); setBodyOverride(""); }} className="text-sm text-slate-500 underline">← Fahrzeug ändern</button>
              </div>
            )}

            {/* SCHRITT 3: TERMIN (NUR MONTAGS) */}
            {step === 2 && (
              <div className="space-y-5">
                <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2"><CalendarCheck size={24} className="text-pink-600" /> Freien Montag wählen</h2>
                <p className="text-sm text-slate-600">Folierungstermine finden ausschließlich <strong>montags</strong> statt — Annahme zwischen 08:00 und 13:00 Uhr.</p>
                {days.length === 0 ? (
                  <p className="text-slate-500">Aktuell sind keine freien Montagstermine verfügbar. Bitte wählen Sie einen späteren Montag oder kontaktieren Sie uns.</p>
                ) : (
                  <div className="grid sm:grid-cols-2 gap-3" data-testid="tint-days">
                    {days.map((d) => (
                      <div key={d.date} className={`rounded-2xl border p-4 bg-white ${selDate === d.date ? "border-pink-500 ring-1 ring-pink-300" : "border-slate-200"}`}>
                        <button onClick={() => { setSelDate(d.date); setSelTime(""); }} className="font-semibold text-slate-900 w-full text-left" data-testid={`tint-day-${d.date}`}>
                          {fmtDate(d.date)}
                        </button>
                        {selDate === d.date && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {d.slots.map((s) => (
                              <button key={s.time} disabled={!s.free} onClick={() => setSelTime(s.time)} data-testid={`tint-slot-${s.time}`}
                                className={`px-3.5 py-2 rounded-lg text-sm font-semibold border ${selTime === s.time ? "bg-pink-600 text-white border-pink-600" : s.free ? "bg-white border-slate-200 hover:border-pink-400" : "bg-slate-100 text-slate-300 border-slate-100 cursor-not-allowed line-through"}`}>
                                {s.time}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex gap-3">
                  <button onClick={() => setStep(1)} className="text-sm text-slate-500 underline">← Zurück</button>
                  <Button onClick={() => selDate && selTime ? setStep(3) : toast.error("Bitte Montag und Uhrzeit wählen")} className="ml-auto h-12 px-8 bg-pink-600 hover:bg-pink-700 text-white font-semibold rounded-xl" data-testid="tint-to-form">
                    Weiter <ArrowRight size={18} className="ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* SCHRITT 4: DATEN + ÜBERSICHT */}
            {step === 3 && quote && (
              <div className="grid lg:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2"><UserCircle size={24} className="text-pink-600" /> Ihre Daten</h2>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="text-sm font-semibold text-slate-700">Vorname *</label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="h-11 bg-white" data-testid="tint-first-name" /></div>
                    <div><label className="text-sm font-semibold text-slate-700">Nachname *</label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="h-11 bg-white" data-testid="tint-last-name" /></div>
                  </div>
                  <div><label className="text-sm font-semibold text-slate-700">Mobiltelefon *</label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-11 bg-white" placeholder="+49 152 …" data-testid="tint-phone" /></div>
                  <div><label className="text-sm font-semibold text-slate-700">E-Mail *</label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11 bg-white" data-testid="tint-email" /></div>
                  <div><label className="text-sm font-semibold text-slate-700">Kennzeichen (optional)</label><Input value={form.license_plate} onChange={(e) => setForm({ ...form, license_plate: e.target.value })} className="h-11 bg-white" placeholder="LU-XX 123" data-testid="tint-plate" /></div>
                  <div><label className="text-sm font-semibold text-slate-700">Bemerkung (optional)</label><Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="bg-white" rows={2} data-testid="tint-note" /></div>
                </div>
                <div>
                  <div className="rounded-2xl bg-white border-2 border-pink-200 p-6 sticky top-24" data-testid="tint-summary">
                    <p className="text-xs font-bold text-pink-600 uppercase tracking-widest">Ihre Buchung</p>
                    <dl className="mt-3 space-y-2 text-sm">
                      <div className="flex justify-between"><dt className="text-slate-500">Fahrzeug</dt><dd className="font-semibold text-slate-900">{make} {model}</dd></div>
                      <div className="flex justify-between"><dt className="text-slate-500">Leistung</dt><dd className="font-semibold text-slate-900 text-right">Tönung ab B-Säule<br />inkl. Heckscheibe</dd></div>
                      <div className="flex justify-between"><dt className="text-slate-500">Preis</dt><dd className="font-bold text-pink-600">{priceStr} inkl. MwSt.</dd></div>
                      <div className="flex justify-between"><dt className="text-slate-500">Termin</dt><dd className="font-semibold text-slate-900">{selDate && fmtDate(selDate)}</dd></div>
                      <div className="flex justify-between"><dt className="text-slate-500">Uhrzeit</dt><dd className="font-semibold text-slate-900">{selTime} Uhr</dd></div>
                      <div className="flex justify-between"><dt className="text-slate-500">Standort</dt><dd className="font-semibold text-slate-900 text-right">Frankenthaler Str. 98,<br />67059 Ludwigshafen</dd></div>
                    </dl>
                    <label className="mt-4 flex items-start gap-2.5 text-sm text-slate-700 cursor-pointer">
                      <Checkbox checked={confirm} onCheckedChange={setConfirm} className="mt-0.5" data-testid="tint-confirm-checkbox" />
                      <span>Ich bestätige, dass ich den ausgewählten Termin <strong>verbindlich</strong> buchen möchte. Es gelten unsere <Link to="/agb" className="underline">AGB</Link> und <Link to="/datenschutz" className="underline">Datenschutzhinweise</Link>.</span>
                    </label>
                    <Button onClick={() => submitBooking(true)} disabled={submitting} className="mt-5 w-full h-12 bg-pink-600 hover:bg-pink-700 text-white font-semibold rounded-xl" data-testid="tint-book-deposit">
                      {submitting ? "Wird gebucht…" : `Termin mit ${depositStr} Anzahlung sichern`}
                    </Button>
                    <Button onClick={() => submitBooking(false)} disabled={submitting} variant="outline" className="mt-2 w-full h-12 rounded-xl border-pink-200 text-pink-700 hover:bg-pink-50 font-semibold" data-testid="tint-book-only">
                      Jetzt verbindlich buchen (Zahlung vor Ort)
                    </Button>
                    <p className="mt-2 text-xs text-slate-500 text-center">25 % Anzahlung: {depositStr} · Restbetrag vor Ort: {restStr}</p>
                  </div>
                  <button onClick={() => setStep(2)} className="mt-3 text-sm text-slate-500 underline">← Termin ändern</button>
                </div>
              </div>
            )}

            {/* SCHRITT 5: BESTÄTIGUNG */}
            {step === 4 && booking && (
              <div className="text-center max-w-lg mx-auto py-6" data-testid="tint-success">
                <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto">
                  <CheckCircle size={36} weight="fill" />
                </div>
                <h2 className="mt-4 text-2xl font-bold text-slate-900">Termin bestätigt!</h2>
                <p className="mt-2 text-slate-600">Vielen Dank! Ihr Termin zur Scheibentönung wurde verbindlich reserviert.</p>
                <div className="mt-5 rounded-2xl bg-white border border-slate-200 p-5 text-left text-sm space-y-1.5">
                  <div className="flex justify-between"><span className="text-slate-500">Buchungsnummer</span><strong data-testid="tint-booking-number">{booking.booking_number}</strong></div>
                  <div className="flex justify-between"><span className="text-slate-500">Fahrzeug</span><strong>{booking.vehicle?.manufacturer} {booking.vehicle?.model}</strong></div>
                  <div className="flex justify-between"><span className="text-slate-500">Preis</span><strong>{booking.price?.toLocaleString("de-DE")} € inkl. MwSt.</strong></div>
                  <div className="flex justify-between"><span className="text-slate-500">Termin</span><strong>{fmtDate(booking.date)}</strong></div>
                  <div className="flex justify-between"><span className="text-slate-500">Uhrzeit</span><strong>{booking.time} Uhr</strong></div>
                  <div className="flex justify-between"><span className="text-slate-500">Standort</span><strong className="text-right">Frankenthaler Str. 98, Ludwigshafen</strong></div>
                </div>
                <p className="mt-4 text-sm text-slate-500">Wir haben Ihnen die Terminbestätigung zusätzlich per E-Mail zugesendet.</p>
                <p className="mt-2 text-sm text-slate-600">
                  Status jederzeit verfolgen:{" "}
                  <Link to={`/status/${booking.booking_number}`} className="text-pink-600 font-semibold underline" data-testid="tint-track-link">
                    {booking.booking_number}
                  </Link>
                </p>
                <Link to="/" className="mt-5 inline-block text-pink-600 font-semibold underline">Zurück zur Startseite</Link>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Vorteile */}
      <section className="py-12 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {BENEFITS.map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2.5 bg-white rounded-xl border border-slate-200 px-4 py-3">
                <Icon size={20} weight="duotone" className="text-pink-600 flex-shrink-0" />
                <span className="text-sm font-medium text-slate-700">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4 Schritte */}
      <section className="py-14 bg-white border-t border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">In vier Schritten zur Scheibentönung</h2>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              ["Fahrzeug auswählen", "Hersteller und Modell auswählen."],
              ["Festpreis sehen", "Preis wird direkt online berechnet."],
              ["Montag buchen", "Freien Montag und Uhrzeit auswählen."],
              ["Fahrzeug bringen", "Wir folieren professionell — Sie übernehmen nichts weiter."],
            ].map(([t, d], i) => (
              <div key={t} className="rounded-2xl border border-slate-200 p-5">
                <div className="w-9 h-9 rounded-full bg-pink-600 text-white flex items-center justify-center font-bold">{i + 1}</div>
                <h3 className="mt-3 font-bold text-slate-900">{t}</h3>
                <p className="mt-1 text-sm text-slate-600">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Qualitätsfolie */}
      <section className="py-14 bg-slate-50 border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">Hochwertige Tönungsfolie statt Billigfolie</h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            Professionelle Tönungsfolie wird von innen auf die Fahrzeugscheibe aufgebracht und speziell an die
            Form der jeweiligen Scheibe angepasst. Abhängig von der konkret eingesetzten Folie kann sie unter anderem
            Sonneneinstrahlung reduzieren, Wärmeeinstrahlung verringern, UV-Strahlung reduzieren, Blendung mindern,
            mehr Privatsphäre schaffen, den Innenraum vor Sonneneinstrahlung schützen — und Ihrem Fahrzeug einen
            deutlich hochwertigeren Look geben.
          </p>
          <p className="mt-3 text-slate-600 leading-relaxed">
            Die genauen technischen Eigenschaften (z.B. Lichtdurchlässigkeit oder bestätigte UV-Werte) hängen vom
            jeweiligen Folienprodukt ab — wir beraten Sie transparent und nennen nur Werte, die vom Hersteller
            für die eingesetzte Folie bestätigt sind.
          </p>
        </div>
      </section>

      {/* FOLIERUNG SCHRITT FÜR SCHRITT (animiert) + VIDEOS */}
      <ProcessShowcase
        eyebrow="Folierung Schritt für Schritt"
        title="So entsteht Ihre Scheibentönung"
        intro="Von der Reinigung bis zur Übergabe der ABG-Unterlagen: So verarbeiten wir Ihre Tönungsfolie — verständlich animiert."
        steps={TOENUNG_STEPS}
        Scene={ToenungScene}
        accent="#db2777"
        soft="#fdf2f8"
        softBorder="#fbcfe8"
        videos={[WORKSHOP_VIDEOS.toenung, WORKSHOP_VIDEOS.folierung]}
        testid="process-toenung"
      />

      {/* VORHER / NACHHER */}
      <section className="py-14 bg-slate-50 border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">Vorher / Nachher: So verändert die Tönung Ihr Auto</h2>
          <p className="mt-3 text-slate-600">Ziehen Sie den Regler und sehen Sie den Unterschied — Tönung ab B-Säule inkl. Heckscheibe.</p>
          <div className="mt-7 max-w-3xl">
            <BeforeAfterSlider
              before="/images/gallery/tint_before.jpg"
              after="/images/gallery/tint_after.jpg"
              altBefore="SUV vor der Scheibentönung mit klaren, ungetönten hinteren Scheiben in der Werkstatt von Servus Autoglas Ludwigshafen"
              altAfter="SUV nach der professionellen Scheibentönung mit dunkel getönten hinteren Seitenscheiben und Heckscheibe — Tönungsfolie mit ABG"
              accent="#db2777"
              testid="tint-before-after"
            />
            <p className="mt-3 text-sm text-slate-500 text-center">Hintere Scheiben getönt — mehr Privatsphäre, weniger Hitze, hochwertiger Look.</p>
          </div>
        </div>
      </section>

      {/* ABG / Recht */}
      <section className="py-14 bg-white border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">Ist Scheibentönung in Deutschland erlaubt?</h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            Ja — für eine nachträgliche Scheibentönung verwenden wir ausschließlich für den vorgesehenen Einsatzzweck
            zugelassene Folien mit entsprechender <strong>Allgemeiner Bauartgenehmigung (ABG)</strong>. Die Genehmigungs-
            bzw. Prüfkennzeichnung der verwendeten Folie bleibt entsprechend den gesetzlichen Vorgaben sichtbar; die
            zugehörigen Unterlagen werden Ihnen ausgehändigt, soweit dies für die verwendete Folie erforderlich ist.
          </p>
          <p className="mt-3 text-slate-600 leading-relaxed">
            Eine nachträgliche Verdunkelung erfolgt grundsätzlich an den dafür zulässigen Scheiben <strong>hinter dem
            Fahrerbereich</strong> — also an den hinteren Seitenscheiben ab der B-Säule und der Heckscheibe. Windschutzscheibe
            und vordere Seitenscheiben tönen wir bewusst nicht.
          </p>
        </div>
      </section>

      {/* AEO Q&A */}
      <section className="py-14 bg-slate-50 border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">Schnelle Antworten</h2>
          <div className="mt-6 grid sm:grid-cols-2 gap-5">
            {AEO_QA.map((f) => (
              <div key={f.q} className="rounded-2xl bg-white border border-slate-200 p-5">
                <h3 className="font-bold text-slate-900">{f.q}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 bg-white border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">Häufige Fragen zur Scheibentönung</h2>
          <div className="mt-6 divide-y divide-slate-200 border border-slate-200 rounded-2xl overflow-hidden" data-testid="tint-faq">
            {FAQS.map((f, i) => (
              <div key={f.q} className="bg-white">
                <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left font-semibold text-slate-900 hover:bg-slate-50">
                  {f.q}
                  <CaretDown size={16} className={`flex-shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                <div className={openFaq === i ? "px-5 pb-4" : "hidden"}>
                  <p className="text-sm text-slate-600 leading-relaxed">{f.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lokal */}
      <section className="py-14 bg-slate-50 border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">Scheibentönung für Ludwigshafen, Mannheim &amp; die Rhein-Neckar-Region</h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            Unsere Werkstatt liegt in der <strong>Frankenthaler Str. 98, 67059 Ludwigshafen</strong> — verkehrsgünstig
            für Kunden aus Ludwigshafen, Mannheim, Frankenthal, Heidelberg und der gesamten Rhein-Neckar-Region.
            Ob Tönungsfolie mit ABG, Sonnenschutzfolie oder mehr Sichtschutz fürs Auto: Sie erhalten bei uns Ihren
            verbindlichen Festpreis online und einen festen Montagstermin — ohne Anfrage-Pingpong.
          </p>
          <p className="mt-4 text-slate-600 leading-relaxed">
            Die häufigste Frage — „Was kostet Scheiben tönen?" — beantworten wir transparent: <strong>ab 250 €
            Festpreis</strong> inkl. Folie, Montage und MwSt., abhängig von der Fahrzeugklasse. Eine professionelle
            Scheibentönung reduziert Hitze im Innenraum, blockt bis zu 99&nbsp;% der UV-Strahlung, schützt Kinder und
            Tiere auf der Rückbank vor Sonne und wertet Ihr Fahrzeug optisch deutlich auf. Übrigens: In unserer{" "}
            <Link to="/sattlerei" className="text-pink-600 font-medium underline">Autosattlerei</Link> kümmern wir uns
            auch um Autohimmel, Cabrioverdecke und Cabrio-Heckscheiben — und bei Steinschlag oder Glasschaden hilft
            unser <Link to="/leistungen" className="text-pink-600 font-medium underline">Autoglas-Service</Link> mit
            Termin innerhalb von 24 Stunden.
          </p>
          <div className="mt-5 flex items-center gap-2 text-slate-700">
            <MapPin size={20} weight="fill" className="text-pink-600" />
            <span className="text-sm font-medium">Servus Autoglas · AutoGlas Erol GmbH · Frankenthaler Str. 98, 67059 Ludwigshafen</span>
          </div>
        </div>
      </section>

      {/* Sticky mobile CTA */}
      {quote && step >= 1 && step < 4 && (
        <div className="fixed bottom-0 inset-x-0 md:hidden bg-white border-t border-pink-200 p-3 flex items-center justify-between gap-3 z-40" data-testid="tint-sticky-cta">
          <div>
            <div className="font-bold text-pink-600">{priceStr} Festpreis</div>
            <div className="text-[11px] text-slate-500">{make} {model} · nur montags</div>
          </div>
          <Button onClick={() => (step === 1 ? loadAvailability() : window.scrollTo({ top: 0, behavior: "smooth" }))} className="h-11 px-5 bg-pink-600 hover:bg-pink-700 text-white font-semibold rounded-xl">
            Montag buchen
          </Button>
        </div>
      )}
    </div>
  );
}
